/**
 * Phase 5 — In-app feedback widget.
 *
 * Floating button bottom-LEFT (FloatingWhatsApp lives bottom-RIGHT;
 * sacred WhatsApp green is reserved for that one button — DO NOT use
 * it here). Click opens a small panel asking "Did you find what you
 * needed?". 👍 closes with a thank-you. 👎 reveals a textarea for the
 * follow-up, which POSTs via tRPC `feedback.submit`.
 *
 * State:
 *   - voom_feedback_session_id  — sessionStorage; stable UUID per visit
 *   - voom_feedback_dismissed   — sessionStorage; close button or submit
 *
 * Route filter: only renders on browse / discovery pages. Hard-hidden
 * on auth, checkout, admin, signup, vendor dashboard so the widget
 * never interrupts a high-friction task.
 *
 * Submit pattern is non-blocking — even on infra failure the panel
 * shows the thanks state. The server returns `{ ok: true }` always;
 * we additionally swallow client-side errors so a bad network never
 * surfaces an error toast.
 */
import { useEffect, useRef, useState, useMemo } from "react";
import { useLocation } from "wouter";
import { AnimatePresence, m } from "framer-motion";
import { MessageSquare, X, ThumbsUp, ThumbsDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";

// ── Storage keys ────────────────────────────────────────────────────────────

const SESSION_KEY = "voom_feedback_session_id";
const DISMISSED_KEY = "voom_feedback_dismissed";

// ── Route filter ────────────────────────────────────────────────────────────

/** Should the widget render on this route? */
function shouldRenderOn(pathname: string): boolean {
  // Hard-hide on these routes — never interrupt a focused task.
  if (pathname.startsWith("/admin")) return false;
  if (pathname.startsWith("/vendor/dashboard")) return false;
  if (pathname === "/sign-in") return false;
  if (pathname.startsWith("/auth")) return false;
  if (pathname === "/reset-password") return false;
  if (pathname.startsWith("/vendor/register")) return false;
  if (pathname.startsWith("/checkout")) return false;

  // Show on browse / discovery only.
  if (pathname === "/") return true;
  if (pathname === "/products") return true;
  if (/^\/products\/[^/]+$/.test(pathname)) return true;
  if (pathname === "/vendors") return true;
  if (/^\/vendors\/[^/]+$/.test(pathname)) return true;
  if (pathname === "/pricing") return true;
  if (pathname.startsWith("/categories")) return true;
  if (/^\/parts\/[^/]+$/.test(pathname)) return true;

  // Everything else: don't render.
  return false;
}

// ── Session helpers ─────────────────────────────────────────────────────────

function getOrCreateSessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const id =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `fb_${Date.now().toString(36)}_${Math.random()
            .toString(36)
            .slice(2, 8)}`;
    sessionStorage.setItem(SESSION_KEY, id);
    return id;
  } catch {
    return `fb_${Date.now().toString(36)}`;
  }
}

function isDismissedThisSession(): boolean {
  try {
    return sessionStorage.getItem(DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
}

function markDismissedThisSession(): void {
  try {
    sessionStorage.setItem(DISMISSED_KEY, "1");
  } catch {
    /* */
  }
}

// ── Component ───────────────────────────────────────────────────────────────

type Step = "ask" | "no-detail" | "thanks";

export default function FeedbackWidget() {
  const [location] = useLocation();
  const enabledForRoute = shouldRenderOn(location);

  // Local UI state.
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>("ask");
  const [freeText, setFreeText] = useState("");
  const [hidden, setHidden] = useState<boolean>(() => isDismissedThisSession());
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Stable session id per visit. Memoised so we don't read sessionStorage
  // on every render. Generated lazily — only on first render that asks.
  const sessionId = useMemo(() => getOrCreateSessionId(), []);

  const submitMutation = trpc.feedback.submit.useMutation();

  // Reset hidden state when the dismissed flag changes (e.g. across tabs
  // in the same session). Not exhaustive but cheap insurance.
  useEffect(() => {
    setHidden(isDismissedThisSession());
  }, [location]);

  // Auto-close the panel ~1.5s after landing on the thanks step.
  useEffect(() => {
    if (step !== "thanks") return;
    closeTimer.current = setTimeout(() => {
      setOpen(false);
      markDismissedThisSession();
      setHidden(true);
    }, 1500);
    return () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, [step]);

  // If the route filter says no, render nothing at all.
  if (!enabledForRoute) return null;

  // Already dismissed (close button or completed submit). Don't show
  // again this session.
  if (hidden) return null;

  function openPanel() {
    setStep("ask");
    setFreeText("");
    setOpen(true);
  }

  function closeAndDismiss() {
    setOpen(false);
    markDismissedThisSession();
    setHidden(true);
  }

  function handleYes() {
    try {
      submitMutation.mutate({
        pageUrl: location,
        answer: "yes",
        sessionId,
      });
    } catch {
      /* non-blocking */
    }
    setStep("thanks");
  }

  function handleNoClick() {
    setStep("no-detail");
  }

  function submitNoDetail() {
    const text = freeText.trim();
    try {
      submitMutation.mutate({
        pageUrl: location,
        answer: "no",
        freeText: text || undefined,
        sessionId,
      });
    } catch {
      /* non-blocking */
    }
    setStep("thanks");
  }

  function skipNoDetail() {
    try {
      submitMutation.mutate({
        pageUrl: location,
        answer: "no",
        sessionId,
      });
    } catch {
      /* non-blocking */
    }
    setStep("thanks");
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Floating button — bottom-LEFT to stay clear of FloatingWhatsApp
          which lives bottom-right. Sits ABOVE the mobile bottom-nav by
          using bottom-20 on mobile, bottom-6 on desktop. */}
      <AnimatePresence>
        {!open && (
          <m.button
            key="feedback-fab"
            type="button"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            onClick={openPanel}
            aria-label="Give feedback"
            data-testid="feedback-widget-fab"
            className="fixed bottom-20 md:bottom-6 left-4 z-30 w-11 h-11 rounded-full bg-[var(--color-brand-red)] hover:bg-[var(--color-brand-red-hover)] text-white shadow-lg hover:shadow-xl active:scale-95 transition-[background-color,transform] duration-150 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-red)] focus:ring-offset-2"
          >
            <MessageSquare className="h-5 w-5" aria-hidden="true" />
          </m.button>
        )}
      </AnimatePresence>

      {/* Slide-up panel. Mobile: nearly full-width strip at bottom-left.
          Desktop/tablet: narrower fixed-width card. */}
      <AnimatePresence>
        {open && (
          <m.div
            key="feedback-panel"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            role="dialog"
            aria-label="Feedback"
            data-testid="feedback-widget-panel"
            className="fixed bottom-20 md:bottom-6 left-4 right-4 md:right-auto md:w-[22rem] z-40 rounded-2xl bg-white border border-[var(--neutral-200)] shadow-xl p-4"
          >
            {/* Close button — always available, top-right of the panel. */}
            <button
              type="button"
              onClick={closeAndDismiss}
              aria-label="Close feedback"
              data-testid="feedback-widget-close"
              className="absolute top-2 right-2 w-8 h-8 rounded-full text-[var(--neutral-500)] hover:text-[var(--neutral-900)] hover:bg-[var(--neutral-100)] flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-red)] focus:ring-offset-1"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>

            {step === "ask" && (
              <div className="pt-1">
                <p className="text-sm font-semibold text-[var(--neutral-900)] pr-8 leading-snug">
                  Did you find what you needed?
                </p>
                <div className="mt-3 flex gap-2">
                  <Button
                    type="button"
                    onClick={handleYes}
                    data-testid="feedback-widget-yes"
                    className="flex-1 min-h-[44px] bg-[var(--color-brand-red)] hover:bg-[var(--color-brand-red-hover)] text-white font-semibold gap-2"
                  >
                    <ThumbsUp className="h-4 w-4" aria-hidden="true" />
                    Yes
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleNoClick}
                    data-testid="feedback-widget-no"
                    className="flex-1 min-h-[44px] border-[var(--neutral-300)] hover:bg-[var(--neutral-50)] font-semibold gap-2"
                  >
                    <ThumbsDown className="h-4 w-4" aria-hidden="true" />
                    No
                  </Button>
                </div>
              </div>
            )}

            {step === "no-detail" && (
              <div className="pt-1">
                <p className="text-sm font-semibold text-[var(--neutral-900)] pr-8 leading-snug">
                  What were you looking for?
                </p>
                <Textarea
                  value={freeText}
                  onChange={e => setFreeText(e.target.value)}
                  maxLength={2000}
                  placeholder="Tell us — we'll fix it"
                  data-testid="feedback-widget-free-text"
                  className="mt-3 min-h-[88px] resize-none border-[var(--neutral-300)] focus-visible:ring-[var(--color-brand-red)] focus-visible:ring-offset-0"
                />
                <div className="mt-3 flex items-center gap-2">
                  <Button
                    type="button"
                    onClick={submitNoDetail}
                    data-testid="feedback-widget-submit"
                    className="flex-1 min-h-[44px] bg-[var(--color-brand-red)] hover:bg-[var(--color-brand-red-hover)] text-white font-semibold"
                  >
                    Send
                  </Button>
                  <button
                    type="button"
                    onClick={skipNoDetail}
                    data-testid="feedback-widget-skip"
                    className="text-xs font-medium text-[var(--neutral-600)] hover:text-[var(--neutral-900)] underline-offset-2 hover:underline px-2 py-2"
                  >
                    Skip
                  </button>
                </div>
              </div>
            )}

            {step === "thanks" && (
              <div className="pt-1 pb-1 pr-8">
                <p className="text-sm font-semibold text-[var(--neutral-900)] leading-snug">
                  {freeText
                    ? "Got it. Thanks!"
                    : submitMutation.variables?.answer === "no"
                      ? "Got it. Thanks!"
                      : "Glad to help. Thanks!"}
                </p>
                <p className="mt-1 text-xs text-[var(--neutral-600)]">
                  We read every note.
                </p>
              </div>
            )}
          </m.div>
        )}
      </AnimatePresence>
    </>
  );
}
